import { useEffect, useState, useCallback, useRef } from "react";
import {
  apiListDrawings,
  apiCreateDrawing,
  apiDeleteDrawing,
  apiDuplicateDrawing,
  apiUpdateDrawing,
  apiLogout,
  isLoggedIn,
  apiListFolders,
  apiCreateFolder,
  apiUpdateFolder,
  apiDeleteFolder,
  apiListTags,
  apiCreateTag,
  apiDeleteTag,
  apiSetDrawingTags,
  apiCreateShareLink,
  apiGetDrawing,
  apiListSharedWithMe,
  apiForkDrawing,
  apiListSharedFoldersWithMe,
} from "../data/api";
import { DrawingCard } from "./DrawingCard";
import { SharedDrawingCard } from "./SharedDrawingCard";
import { DashboardBanner } from "./DashboardBanner";
import { ProfileDialog, getUserInitials } from "./ProfileDialog";
import { ShareInternalDialog } from "./ShareInternalDialog";
import { ShareFolderDialog } from "./ShareFolderDialog";
import { SharedFolderBrowser } from "./SharedFolderBrowser";
import { AccessListDialog } from "./AccessListDialog";

import type {
  ApiUser,
  DrawingMetadata,
  FolderInfo,
  TagInfo,
  SharedWithMeDrawing,
  SharedWithMeFolder,
} from "../data/api";

import "./DrawingsDashboard.scss";

interface DrawingsDashboardProps {
  user: ApiUser | null;
  onOpenDrawing: (id: string) => void;
  onNewLocalDrawing: () => void;
  onLogout: () => void;
  onAdmin?: () => void;
  onUserUpdated?: (user: ApiUser) => void;
}

type SelectedItem =
  | { type: "drawing"; id: string }
  | { type: "folder"; id: string };

function buildBreadcrumbs(
  folderId: string | null,
  folders: FolderInfo[],
): FolderInfo[] {
  const crumbs: FolderInfo[] = [];
  let current = folderId;
  while (current) {
    const folder = folders.find((f) => f.id === current);
    if (!folder) break;
    crumbs.unshift(folder);
    current = folder.parent_id;
  }
  return crumbs;
}

// Build a flat list of folders as a tree for the "Move to" picker
function buildFolderTree(
  folders: FolderInfo[],
  parentId: string | null,
  depth: number,
): Array<{ folder: FolderInfo; depth: number }> {
  const children = folders
    .filter((f) => f.parent_id === parentId)
    .sort((a, b) => a.name.localeCompare(b.name));
  const result: Array<{ folder: FolderInfo; depth: number }> = [];
  for (const child of children) {
    result.push({ folder: child, depth });
    result.push(...buildFolderTree(folders, child.id, depth + 1));
  }
  return result;
}

export const DrawingsDashboard = ({
  user,
  onOpenDrawing,
  onNewLocalDrawing,
  onAdmin,
  onLogout,
  onUserUpdated,
}: DrawingsDashboardProps) => {
  const [showProfile, setShowProfile] = useState(false);
  const [shareInternalTarget, setShareInternalTarget] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [shareFolderTarget, setShareFolderTarget] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [browsingSharedFolder, setBrowsingSharedFolder] =
    useState<SharedWithMeFolder | null>(null);
  const [accessTarget, setAccessTarget] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [drawings, setDrawings] = useState<DrawingMetadata[]>([]);
  const [folders, setFolders] = useState<FolderInfo[]>([]);
  const [tags, setTags] = useState<TagInfo[]>([]);
  const [sharedDrawings, setSharedDrawings] = useState<SharedWithMeDrawing[]>(
    [],
  );
  const [sharedFolders, setSharedFolders] = useState<SharedWithMeFolder[]>([]);
  const [refreshingShared, setRefreshingShared] = useState(false);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(() => {
    return sessionStorage.getItem("excalidraw-last-folder-id") || null;
  });
  const [dragOverFolderId, setDragOverFolderId] = useState<
    string | null | "root"
  >(null);

  // Tag management modal
  const [tagModalDrawingId, setTagModalDrawingId] = useState<string | null>(
    null,
  );
  const [tagModalSelected, setTagModalSelected] = useState<string[]>([]);
  const [tagModalSearch, setTagModalSearch] = useState("");

  // Tag filter panel toggle
  const [showTagFilter, setShowTagFilter] = useState(false);

  // New tag input
  const [showNewTag, setShowNewTag] = useState(false);
  const [newTagName, setNewTagName] = useState("");
  const [newTagColor, setNewTagColor] = useState("#6965db");

  // Folder rename
  const [renamingFolderId, setRenamingFolderId] = useState<string | null>(null);
  const [renameFolderName, setRenameFolderName] = useState("");

  // Multi-select
  const [selectMode, setSelectMode] = useState(false);
  const [selectedItems, setSelectedItems] = useState<SelectedItem[]>([]);

  // Share link feedback
  const [shareLinkCopied, setShareLinkCopied] = useState(false);

  // Create dropdown
  const [showCreateMenu, setShowCreateMenu] = useState(false);

  // Folder context menu
  const [folderMenuId, setFolderMenuId] = useState<string | null>(null);

  // Move-to modal (single item or bulk)
  const [moveModalTarget, setMoveModalTarget] = useState<
    | { type: "single-drawing"; id: string }
    | { type: "single-folder"; id: string }
    | { type: "bulk" }
    | null
  >(null);
  const [moveTargetFolderId, setMoveTargetFolderId] = useState<string | null>(
    null,
  );

  const gridRef = useRef<HTMLDivElement>(null);

  const loadAll = useCallback(async () => {
    if (!isLoggedIn()) {
      setLoading(false);
      return;
    }
    try {
      // shared-with-me é isolado com .catch para não derrubar o dashboard
      // inteiro caso a chamada falhe.
      const [
        drawingsList,
        foldersList,
        tagsList,
        sharedList,
        sharedFoldersList,
      ] = await Promise.all([
        apiListDrawings(),
        apiListFolders(),
        apiListTags(),
        apiListSharedWithMe().catch(() => []),
        apiListSharedFoldersWithMe().catch(() => []),
      ]);
      setDrawings(drawingsList);
      setFolders(foldersList);
      setTags(tagsList);
      setSharedDrawings(sharedList);
      setSharedFolders(sharedFoldersList);
    } catch (err) {
      console.error("Failed to load data:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // Recarrega só os "Compartilhados comigo" — acionado pelo botão de atualizar
  // ao lado do título da seção (sem polling, para não pesar o front-end).
  const refreshShared = useCallback(async () => {
    if (!isLoggedIn()) {
      return;
    }
    setRefreshingShared(true);
    try {
      const [sharedList, sharedFoldersList] = await Promise.all([
        apiListSharedWithMe(),
        apiListSharedFoldersWithMe(),
      ]);
      setSharedDrawings(sharedList);
      setSharedFolders(sharedFoldersList);
    } catch {
      // silencioso — mantém a lista atual em caso de falha de rede
    } finally {
      setRefreshingShared(false);
    }
  }, []);

  // Abre um desenho compartilhado em modo somente-leitura (flag lido pelo App.tsx).
  const handleOpenShared = useCallback(
    (id: string) => {
      sessionStorage.setItem("excalidraw-open-as-view-only", id);
      onOpenDrawing(id);
    },
    [onOpenDrawing],
  );

  // Cria uma cópia editável (fork) de um desenho compartilhado e a abre.
  const handleSaveCopyShared = useCallback(
    async (id: string) => {
      try {
        const copy = await apiForkDrawing(id);
        sessionStorage.removeItem("excalidraw-open-as-view-only");
        onOpenDrawing(copy.id);
      } catch (err) {
        console.error("Failed to fork shared drawing:", err);
      }
    },
    [onOpenDrawing],
  );

  // Abre um desenho de dentro de uma pasta compartilhada. Com acesso de edição
  // abre normalmente (o backend valida a escrita por herança); senão, leitura.
  const handleOpenSharedFolderDrawing = useCallback(
    (id: string, editable: boolean) => {
      if (editable) {
        sessionStorage.removeItem("excalidraw-open-as-view-only");
      } else {
        sessionStorage.setItem("excalidraw-open-as-view-only", id);
      }
      onOpenDrawing(id);
    },
    [onOpenDrawing],
  );

  useEffect(() => {
    if (currentFolderId) {
      sessionStorage.setItem("excalidraw-last-folder-id", currentFolderId);
    } else {
      sessionStorage.removeItem("excalidraw-last-folder-id");
    }
  }, [currentFolderId]);

  // --- Drawings ---
  const handleCreate = async () => {
    if (!isLoggedIn()) {
      onNewLocalDrawing();
      return;
    }
    try {
      const drawing = await apiCreateDrawing({
        name: "Novo quadro",
        folderId: currentFolderId,
      });
      setDrawings((prev) => [drawing, ...prev]);
      onOpenDrawing(drawing.id);
    } catch (err) {
      console.error("Failed to create drawing:", err);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Tem certeza que deseja excluir este quadro?")) return;
    try {
      await apiDeleteDrawing(id);
      setDrawings((prev) => prev.filter((d) => d.id !== id));
    } catch (err) {
      console.error("Failed to delete drawing:", err);
    }
  };

  const handleDuplicate = async (id: string) => {
    try {
      const drawing = await apiDuplicateDrawing(id);
      setDrawings((prev) => [drawing, ...prev]);
    } catch (err) {
      console.error("Failed to duplicate drawing:", err);
    }
  };

  const handleShareViewLink = async (id: string) => {
    try {
      const share = await apiCreateShareLink(id);
      const url = `${window.location.origin}#view=${share.share_token}`;
      await navigator.clipboard.writeText(url);
      setShareLinkCopied(true);
      setTimeout(() => setShareLinkCopied(false), 3000);
    } catch (err) {
      console.error("Failed to create share link:", err);
      alert("Erro ao gerar link de visualizacao.");
    }
  };

  const handleRename = async (id: string, newName: string) => {
    try {
      await apiUpdateDrawing(id, { name: newName });
      setDrawings((prev) =>
        prev.map((d) => (d.id === id ? { ...d, name: newName } : d)),
      );
    } catch (err) {
      console.error("Failed to rename drawing:", err);
    }
  };

  const handleMoveDrawing = async (
    drawingId: string,
    folderId: string | null,
  ) => {
    try {
      await apiUpdateDrawing(drawingId, { folderId });
      setDrawings((prev) =>
        prev.map((d) =>
          d.id === drawingId ? { ...d, folder_id: folderId } : d,
        ),
      );
    } catch (err) {
      console.error("Failed to move drawing:", err);
    }
  };

  // --- Folders ---
  const handleCreateFolder = async () => {
    try {
      const folder = await apiCreateFolder({
        name: "Nova pasta",
        parentId: currentFolderId,
      });
      setFolders((prev) => [...prev, folder]);
      setRenamingFolderId(folder.id);
      setRenameFolderName(folder.name);
    } catch (err) {
      console.error("Failed to create folder:", err);
    }
  };

  const handleRenameFolder = async (id: string, name: string) => {
    if (!name.trim()) {
      setRenamingFolderId(null);
      return;
    }
    try {
      await apiUpdateFolder(id, { name: name.trim() });
      setFolders((prev) =>
        prev.map((f) => (f.id === id ? { ...f, name: name.trim() } : f)),
      );
    } catch (err) {
      console.error("Failed to rename folder:", err);
    }
    setRenamingFolderId(null);
  };

  const handleMoveFolder = async (
    folderId: string,
    newParentId: string | null,
  ) => {
    if (folderId === newParentId) return;
    try {
      await apiUpdateFolder(folderId, { parentId: newParentId });
      setFolders((prev) =>
        prev.map((f) =>
          f.id === folderId ? { ...f, parent_id: newParentId } : f,
        ),
      );
    } catch (err) {
      console.error("Failed to move folder:", err);
    }
  };

  const handleDeleteFolder = async (id: string) => {
    if (
      !confirm(
        "Excluir esta pasta? Os quadros dentro serão movidos para a raiz.",
      )
    )
      return;
    try {
      await apiDeleteFolder(id);
      setDrawings((prev) =>
        prev.map((d) => (d.folder_id === id ? { ...d, folder_id: null } : d)),
      );
      const deletedFolder = folders.find((f) => f.id === id);
      setFolders((prev) =>
        prev
          .filter((f) => f.id !== id)
          .map((f) =>
            f.parent_id === id
              ? { ...f, parent_id: deletedFolder?.parent_id || null }
              : f,
          ),
      );
    } catch (err) {
      console.error("Failed to delete folder:", err);
    }
  };

  // --- Tags ---
  const handleCreateTag = async () => {
    if (!newTagName.trim()) return;
    try {
      const tag = await apiCreateTag({
        name: newTagName.trim(),
        color: newTagColor,
      });
      setTags((prev) => [...prev, tag]);
      setNewTagName("");
      setShowNewTag(false);
    } catch (err) {
      console.error("Failed to create tag:", err);
    }
  };

  const handleDeleteTag = async (id: string) => {
    try {
      await apiDeleteTag(id);
      setTags((prev) => prev.filter((t) => t.id !== id));
      setSelectedTags((prev) => prev.filter((tid) => tid !== id));
      setDrawings((prev) =>
        prev.map((d) => ({
          ...d,
          tags: d.tags.filter((t) => t.id !== id),
        })),
      );
    } catch (err) {
      console.error("Failed to delete tag:", err);
    }
  };

  const handleSetDrawingTags = async (drawingId: string, tagIds: string[]) => {
    try {
      const updatedTags = await apiSetDrawingTags(drawingId, tagIds);
      setDrawings((prev) =>
        prev.map((d) => (d.id === drawingId ? { ...d, tags: updatedTags } : d)),
      );
    } catch (err) {
      console.error("Failed to set tags:", err);
    }
  };

  const openTagModal = (drawingId: string) => {
    const drawing = drawings.find((d) => d.id === drawingId);
    setTagModalDrawingId(drawingId);
    setTagModalSelected(drawing?.tags.map((t) => t.id) || []);
  };

  const closeTagModal = () => {
    if (tagModalDrawingId) {
      handleSetDrawingTags(tagModalDrawingId, tagModalSelected);
    }
    setTagModalDrawingId(null);
    setTagModalSearch("");
  };

  const toggleTagInModal = (tagId: string) => {
    setTagModalSelected((prev) =>
      prev.includes(tagId)
        ? prev.filter((id) => id !== tagId)
        : [...prev, tagId],
    );
  };

  // --- Multi-select ---
  const toggleSelectMode = () => {
    if (selectMode) {
      setSelectedItems([]);
    }
    setSelectMode(!selectMode);
  };

  const toggleSelectDrawing = (id: string) => {
    setSelectedItems((prev) => {
      const exists = prev.find((i) => i.type === "drawing" && i.id === id);
      if (exists)
        return prev.filter((i) => !(i.type === "drawing" && i.id === id));
      return [...prev, { type: "drawing", id }];
    });
  };

  const toggleSelectFolder = (id: string) => {
    setSelectedItems((prev) => {
      const exists = prev.find((i) => i.type === "folder" && i.id === id);
      if (exists)
        return prev.filter((i) => !(i.type === "folder" && i.id === id));
      return [...prev, { type: "folder", id }];
    });
  };

  const isItemSelected = (type: "drawing" | "folder", id: string) =>
    selectedItems.some((i) => i.type === type && i.id === id);

  const selectAll = () => {
    const allItems: SelectedItem[] = [
      ...filteredFolders.map((f) => ({ type: "folder" as const, id: f.id })),
      ...filteredDrawings.map((d) => ({ type: "drawing" as const, id: d.id })),
    ];
    setSelectedItems(allItems);
  };

  const deselectAll = () => {
    setSelectedItems([]);
  };

  // --- Bulk actions ---
  const handleBulkDelete = async () => {
    const drawingCount = selectedItems.filter(
      (i) => i.type === "drawing",
    ).length;
    const folderCount = selectedItems.filter((i) => i.type === "folder").length;
    const parts: string[] = [];
    if (drawingCount > 0)
      parts.push(`${drawingCount} quadro${drawingCount > 1 ? "s" : ""}`);
    if (folderCount > 0)
      parts.push(`${folderCount} pasta${folderCount > 1 ? "s" : ""}`);

    if (!confirm(`Excluir ${parts.join(" e ")}?`)) return;

    for (const item of selectedItems) {
      try {
        if (item.type === "drawing") {
          await apiDeleteDrawing(item.id);
          setDrawings((prev) => prev.filter((d) => d.id !== item.id));
        } else {
          await apiDeleteFolder(item.id);
          setDrawings((prev) =>
            prev.map((d) =>
              d.folder_id === item.id ? { ...d, folder_id: null } : d,
            ),
          );
          const deletedFolder = folders.find((f) => f.id === item.id);
          setFolders((prev) =>
            prev
              .filter((f) => f.id !== item.id)
              .map((f) =>
                f.parent_id === item.id
                  ? { ...f, parent_id: deletedFolder?.parent_id || null }
                  : f,
              ),
          );
        }
      } catch (err) {
        console.error(`Failed to delete ${item.type} ${item.id}:`, err);
      }
    }
    setSelectedItems([]);
    setSelectMode(false);
  };

  const handleBulkMoveTo = () => {
    setMoveModalTarget({ type: "bulk" });
    setMoveTargetFolderId(null);
  };

  const handleBulkDownload = async () => {
    const drawingItems = selectedItems.filter((i) => i.type === "drawing");
    if (drawingItems.length === 0) return;

    for (const item of drawingItems) {
      try {
        const full = await apiGetDrawing(item.id);
        const payload = {
          type: "excalidraw",
          version: 2,
          source: window.location.origin,
          elements: full.elements || [],
          appState: full.app_state || full.appState || {},
          files: full.files || {},
        };
        const blob = new Blob([JSON.stringify(payload, null, 2)], {
          type: "application/vnd.excalidraw+json",
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${full.name || "drawing"}.excalidraw`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      } catch (err) {
        console.error(`Failed to download drawing ${item.id}:`, err);
      }
    }
  };

  // Card-level select: entering selectMode automatically when a checkbox is clicked outside select mode
  const handleCardSelect = (id: string) => {
    if (!selectMode) {
      setSelectMode(true);
      setSelectedItems([{ type: "drawing", id }]);
    } else {
      toggleSelectDrawing(id);
    }
  };

  // --- Move-to modal ---
  const openMoveModal = (
    type: "single-drawing" | "single-folder",
    id: string,
  ) => {
    setMoveModalTarget({ type, id });
    setMoveTargetFolderId(null);
  };

  const isDescendant = (folderId: string, ancestorId: string): boolean => {
    let current: string | null = folderId;
    while (current) {
      if (current === ancestorId) return true;
      const f = folders.find((fl) => fl.id === current);
      current = f?.parent_id || null;
    }
    return false;
  };

  const getExcludedFolderIds = (): Set<string> => {
    const excluded = new Set<string>();
    if (!moveModalTarget) return excluded;

    if (moveModalTarget.type === "single-folder") {
      excluded.add(moveModalTarget.id);
      // Exclude all descendants
      for (const f of folders) {
        if (isDescendant(f.id, moveModalTarget.id)) {
          excluded.add(f.id);
        }
      }
    } else if (moveModalTarget.type === "bulk") {
      for (const item of selectedItems) {
        if (item.type === "folder") {
          excluded.add(item.id);
          for (const f of folders) {
            if (isDescendant(f.id, item.id)) {
              excluded.add(f.id);
            }
          }
        }
      }
    }
    return excluded;
  };

  const executeMoveToFolder = async () => {
    if (!moveModalTarget) return;

    if (moveModalTarget.type === "single-drawing") {
      await handleMoveDrawing(moveModalTarget.id, moveTargetFolderId);
    } else if (moveModalTarget.type === "single-folder") {
      await handleMoveFolder(moveModalTarget.id, moveTargetFolderId);
    } else if (moveModalTarget.type === "bulk") {
      for (const item of selectedItems) {
        try {
          if (item.type === "drawing") {
            await handleMoveDrawing(item.id, moveTargetFolderId);
          } else {
            await handleMoveFolder(item.id, moveTargetFolderId);
          }
        } catch (err) {
          console.error(`Failed to move ${item.type} ${item.id}:`, err);
        }
      }
      setSelectedItems([]);
      setSelectMode(false);
    }
    setMoveModalTarget(null);
  };

  // --- Filtering ---
  const toggleFilterTag = (tagId: string) => {
    setSelectedTags((prev) =>
      prev.includes(tagId)
        ? prev.filter((id) => id !== tagId)
        : [...prev, tagId],
    );
  };

  const currentSubfolders = folders.filter(
    (f) => f.parent_id === currentFolderId,
  );

  const isSearching = search.length > 0 || selectedTags.length > 0;

  const filteredFolders = isSearching
    ? folders.filter((f) => {
        if (!search) return false;
        return f.name.toLowerCase().includes(search.toLowerCase());
      })
    : currentSubfolders;

  const filteredDrawings = drawings.filter((d) => {
    if (!isSearching && d.folder_id !== currentFolderId) return false;

    if (search) {
      const q = search.toLowerCase();
      const nameMatch = d.name.toLowerCase().includes(q);
      const tagMatch = d.tags.some((t) => t.name.toLowerCase().includes(q));
      if (!nameMatch && !tagMatch) return false;
    }

    if (selectedTags.length > 0) {
      const drawingTagIds = d.tags.map((t) => t.id);
      if (!selectedTags.every((tid) => drawingTagIds.includes(tid)))
        return false;
    }

    return true;
  });

  const breadcrumbs = buildBreadcrumbs(currentFolderId, folders);

  const handleLogout = () => {
    apiLogout();
    onLogout();
  };

  // --- Drag & Drop handlers ---
  const handleFolderDragOver = (
    e: React.DragEvent,
    folderId: string | null,
  ) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverFolderId(folderId ?? "root");
  };

  const handleFolderDragLeave = () => {
    setDragOverFolderId(null);
  };

  const handleFolderDrop = (
    e: React.DragEvent,
    targetFolderId: string | null,
  ) => {
    e.preventDefault();
    setDragOverFolderId(null);

    const drawingId = e.dataTransfer.getData("application/x-drawing-id");
    const sourceFolderId = e.dataTransfer.getData("application/x-folder-id");

    if (drawingId) {
      handleMoveDrawing(drawingId, targetFolderId);
    } else if (sourceFolderId && sourceFolderId !== targetFolderId) {
      handleMoveFolder(sourceFolderId, targetFolderId);
    }
  };

  const handleFolderItemDragStart = (e: React.DragEvent, folderId: string) => {
    e.dataTransfer.setData("application/x-folder-id", folderId);
    e.dataTransfer.effectAllowed = "move";
  };

  const TAG_COLORS = [
    "#6965db",
    "#e03131",
    "#2f9e44",
    "#f08c00",
    "#1971c2",
    "#9c36b5",
    "#0c8599",
    "#e8590c",
    "#5c940d",
    "#862e9c",
  ];

  const folderTree = buildFolderTree(folders, null, 0);
  const excludedIds = moveModalTarget
    ? getExcludedFolderIds()
    : new Set<string>();

  return (
    <div className="drawings-dashboard">
      <div className="dashboard-shell">
        <aside className="dashboard-sidebar">
          <div className="dashboard-sidebar__top">
            {user && (
              <button
                className="dashboard-sidebar__avatar"
                onClick={() => setShowProfile(true)}
                title="Perfil"
                aria-label="Abrir perfil"
              >
                {user.avatar ? (
                  <img src={user.avatar} alt={user.name || user.email} />
                ) : (
                  <span>{getUserInitials(user)}</span>
                )}
              </button>
            )}

            <div className="dashboard-dropdown">
              <button
                className="dashboard-sidebar__btn dashboard-sidebar__btn--primary"
                onClick={() => setShowCreateMenu(!showCreateMenu)}
                title="Criar"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                >
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
                <span className="dashboard-sidebar__btn-label">Criar</span>
              </button>
              {showCreateMenu && (
                <div
                  className="dashboard-dropdown__menu dashboard-dropdown__menu--sidebar"
                  onMouseLeave={() => setShowCreateMenu(false)}
                >
                  <button
                    onClick={() => {
                      handleCreate();
                      setShowCreateMenu(false);
                    }}
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <rect x="3" y="3" width="18" height="18" rx="2" />
                      <path d="M9 3v18M3 9h18" />
                    </svg>
                    Novo Quadro
                  </button>
                  <button
                    onClick={() => {
                      handleCreateFolder();
                      setShowCreateMenu(false);
                    }}
                  >
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2v11z" />
                    </svg>
                    Nova Pasta
                  </button>
                </div>
              )}
            </div>

            <button
              className={`dashboard-sidebar__btn ${
                selectMode ? "dashboard-sidebar__btn--active" : ""
              }`}
              onClick={toggleSelectMode}
              title={selectMode ? "Cancelar seleção" : "Selecionar quadros"}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <polyline points="9 11 12 14 22 4" />
                <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" />
              </svg>
              <span className="dashboard-sidebar__btn-label">
                {selectMode ? "Cancelar" : "Selecionar"}
              </span>
            </button>

            {user?.is_admin && onAdmin && (
              <button
                className="dashboard-sidebar__btn"
                onClick={onAdmin}
                title="Painel de administração"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="12" cy="12" r="3" />
                  <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" />
                </svg>
                <span className="dashboard-sidebar__btn-label">Admin</span>
              </button>
            )}
          </div>
        </aside>

        <main className="dashboard-main">
          <DashboardBanner />

          <div className="dashboard-search-section">
            <div className="dashboard-search-wrapper">
              <svg
                className="dashboard-search-icon"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.35-4.35" />
              </svg>
              <input
                type="text"
                placeholder="Busque por nome ou tag..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="dashboard-search"
              />
              {search && (
                <button
                  className="dashboard-search-clear"
                  onClick={() => setSearch("")}
                >
                  &times;
                </button>
              )}
            </div>
          </div>

          {/* Bulk action bar */}
          {selectMode && (
            <div className="dashboard-bulk-bar">
              <div className="dashboard-bulk-bar__info">
                {selectedItems.length} item
                {selectedItems.length !== 1 ? "s" : ""} selecionado
                {selectedItems.length !== 1 ? "s" : ""}
              </div>
              <div className="dashboard-bulk-bar__actions">
                <button onClick={selectAll} className="dashboard-bulk-btn">
                  Selecionar tudo
                </button>
                {selectedItems.length > 0 && (
                  <>
                    <button
                      onClick={deselectAll}
                      className="dashboard-bulk-btn"
                    >
                      Limpar
                    </button>
                    <button
                      onClick={handleBulkMoveTo}
                      className="dashboard-bulk-btn dashboard-bulk-btn--primary"
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2v11z" />
                      </svg>
                      Mover para...
                    </button>
                    {selectedItems.some((i) => i.type === "drawing") && (
                      <button
                        onClick={handleBulkDownload}
                        className="dashboard-bulk-btn"
                      >
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                          <polyline points="7 10 12 15 17 10" />
                          <line x1="12" y1="15" x2="12" y2="3" />
                        </svg>
                        Baixar
                      </button>
                    )}
                    <button
                      onClick={handleBulkDelete}
                      className="dashboard-bulk-btn dashboard-bulk-btn--danger"
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <polyline points="3 6 5 6 21 6" />
                        <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" />
                      </svg>
                      Excluir
                    </button>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Tag filter bar */}
          {showTagFilter && (
            <div className="dashboard-tags-bar">
              <div className="dashboard-tags-bar__list">
                {tags.map((tag) => (
                  <button
                    key={tag.id}
                    className={`dashboard-tag-chip ${
                      selectedTags.includes(tag.id)
                        ? "dashboard-tag-chip--active"
                        : ""
                    }`}
                    style={
                      {
                        "--tag-color": tag.color,
                        "--tag-bg": tag.color + "18",
                        "--tag-bg-active": tag.color + "30",
                      } as React.CSSProperties
                    }
                    onClick={() => toggleFilterTag(tag.id)}
                  >
                    <span
                      className="dashboard-tag-chip__dot"
                      style={{ backgroundColor: tag.color }}
                    />
                    {tag.name}
                    <button
                      className="dashboard-tag-chip__delete"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (confirm(`Excluir a tag "${tag.name}"?`))
                          handleDeleteTag(tag.id);
                      }}
                      title="Excluir tag"
                    >
                      &times;
                    </button>
                  </button>
                ))}
                {showNewTag ? (
                  <div className="dashboard-new-tag-form">
                    <input
                      type="text"
                      value={newTagName}
                      onChange={(e) => setNewTagName(e.target.value)}
                      placeholder="Nome da tag"
                      className="dashboard-new-tag-input"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleCreateTag();
                        if (e.key === "Escape") {
                          setShowNewTag(false);
                          setNewTagName("");
                        }
                      }}
                    />
                    <div className="dashboard-new-tag-colors">
                      {TAG_COLORS.map((c) => (
                        <button
                          key={c}
                          className={`dashboard-color-btn ${
                            newTagColor === c
                              ? "dashboard-color-btn--active"
                              : ""
                          }`}
                          style={{ backgroundColor: c }}
                          onClick={() => setNewTagColor(c)}
                        />
                      ))}
                    </div>
                    <button
                      className="dashboard-new-tag-save"
                      onClick={handleCreateTag}
                    >
                      Criar
                    </button>
                    <button
                      className="dashboard-new-tag-cancel"
                      onClick={() => {
                        setShowNewTag(false);
                        setNewTagName("");
                      }}
                    >
                      &times;
                    </button>
                  </div>
                ) : (
                  <button
                    className="dashboard-tag-chip dashboard-tag-chip--add"
                    onClick={() => setShowNewTag(true)}
                  >
                    + Tag
                  </button>
                )}
              </div>
              {selectedTags.length > 0 && (
                <button
                  className="dashboard-tags-bar__clear"
                  onClick={() => setSelectedTags([])}
                >
                  Limpar filtros
                </button>
              )}
            </div>
          )}

          {/* Breadcrumb navigation with inline back button */}
          {currentFolderId &&
            !isSearching &&
            (() => {
              const parentFolder = folders.find(
                (f) => f.id === currentFolderId,
              );
              const parentId = parentFolder?.parent_id || null;
              const expectedDragKey = parentId ?? "root";
              const isBackDragOver = dragOverFolderId === expectedDragKey;
              return (
                <div className="dashboard-breadcrumbs">
                  <button
                    className={`dashboard-breadcrumbs__back ${
                      isBackDragOver
                        ? "dashboard-breadcrumbs__back--drag-over"
                        : ""
                    }`}
                    onClick={() => setCurrentFolderId(parentId)}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.dataTransfer.dropEffect = "move";
                      setDragOverFolderId(expectedDragKey);
                    }}
                    onDragLeave={handleFolderDragLeave}
                    onDrop={(e) => handleFolderDrop(e, parentId)}
                    title="Voltar"
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.2"
                    >
                      <path d="M15 18l-6-6 6-6" />
                    </svg>
                    <span>Voltar</span>
                  </button>
                  <div className="dashboard-breadcrumbs__trail">
                    <button
                      onClick={() => setCurrentFolderId(null)}
                      className="dashboard-breadcrumb"
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                      </svg>
                      Raiz
                    </button>
                    {breadcrumbs.map((folder, i) => (
                      <span
                        key={folder.id}
                        className="dashboard-breadcrumbs__crumb"
                      >
                        <svg
                          className="dashboard-breadcrumb-sep"
                          width="12"
                          height="12"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.2"
                        >
                          <path d="M9 18l6-6-6-6" />
                        </svg>
                        {i === breadcrumbs.length - 1 ? (
                          <span className="dashboard-breadcrumb dashboard-breadcrumb--current">
                            {folder.name}
                          </span>
                        ) : (
                          <button
                            onClick={() => setCurrentFolderId(folder.id)}
                            className="dashboard-breadcrumb"
                          >
                            {folder.name}
                          </button>
                        )}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })()}

          {isSearching &&
            (filteredDrawings.length > 0 || filteredFolders.length > 0) && (
              <div className="dashboard-search-info">
                Mostrando {filteredFolders.length + filteredDrawings.length}{" "}
                resultado
                {filteredFolders.length + filteredDrawings.length !== 1
                  ? "s"
                  : ""}
                {filteredFolders.length > 0 &&
                  ` (${filteredFolders.length} pasta${
                    filteredFolders.length !== 1 ? "s" : ""
                  })`}
              </div>
            )}

          <div className="dashboard-section-header">
            <h2 className="dashboard-section-header__title">
              {isSearching
                ? "Resultados"
                : currentFolderId
                ? breadcrumbs[breadcrumbs.length - 1]?.name || "Recentes"
                : "Recentes"}
            </h2>
            <div className="dashboard-section-header__actions">
              <button
                className={`dashboard-filter-btn ${
                  showTagFilter || selectedTags.length > 0
                    ? "dashboard-filter-btn--active"
                    : ""
                }`}
                onClick={() => setShowTagFilter(!showTagFilter)}
                title="Filtrar por tags"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
                </svg>
                <span>Tags</span>
                {selectedTags.length > 0 && (
                  <span className="dashboard-filter-btn__badge">
                    {selectedTags.length}
                  </span>
                )}
              </button>
            </div>
          </div>

          {loading ? (
            <div className="dashboard-loading">Carregando quadros...</div>
          ) : (
            <div className="dashboard-content" ref={gridRef}>
              {/* Sub-folders */}
              {filteredFolders.map((folder) => (
                <div
                  key={folder.id}
                  className={`dashboard-folder-item ${
                    dragOverFolderId === folder.id
                      ? "dashboard-folder-item--drag-over"
                      : ""
                  } ${
                    isItemSelected("folder", folder.id)
                      ? "dashboard-folder-item--selected"
                      : ""
                  } ${
                    folderMenuId === folder.id
                      ? "dashboard-folder-item--menu-open"
                      : ""
                  }`}
                  draggable={!selectMode}
                  onDragStart={(e) => handleFolderItemDragStart(e, folder.id)}
                  onDragOver={(e) => handleFolderDragOver(e, folder.id)}
                  onDragLeave={handleFolderDragLeave}
                  onDrop={(e) => handleFolderDrop(e, folder.id)}
                  onDoubleClick={() =>
                    !selectMode && setCurrentFolderId(folder.id)
                  }
                  onClick={() => selectMode && toggleSelectFolder(folder.id)}
                >
                  {selectMode && (
                    <div className="dashboard-folder-item__checkbox">
                      <input
                        type="checkbox"
                        checked={isItemSelected("folder", folder.id)}
                        onChange={() => toggleSelectFolder(folder.id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                  )}
                  <div className="dashboard-folder-item__icon">
                    <svg
                      width="32"
                      height="32"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                      stroke="none"
                    >
                      <path d="M2 6a2 2 0 012-2h5l2 2h9a2 2 0 012 2v10a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
                    </svg>
                  </div>
                  {renamingFolderId === folder.id ? (
                    <input
                      type="text"
                      value={renameFolderName}
                      onChange={(e) => setRenameFolderName(e.target.value)}
                      onBlur={() =>
                        handleRenameFolder(folder.id, renameFolderName)
                      }
                      onKeyDown={(e) => {
                        if (e.key === "Enter")
                          handleRenameFolder(folder.id, renameFolderName);
                        if (e.key === "Escape") setRenamingFolderId(null);
                      }}
                      autoFocus
                      className="dashboard-folder-item__name-input"
                      onClick={(e) => e.stopPropagation()}
                    />
                  ) : (
                    <span className="dashboard-folder-item__name">
                      {folder.name}
                    </span>
                  )}
                  <span className="dashboard-folder-item__count">
                    {drawings.filter((d) => d.folder_id === folder.id).length}
                  </span>
                  {!selectMode && (
                    <div className="dashboard-folder-item__actions">
                      <button
                        className="dashboard-folder-item__menu-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setFolderMenuId(
                            folderMenuId === folder.id ? null : folder.id,
                          );
                        }}
                        title="Menu"
                      >
                        &#8942;
                      </button>
                      {folderMenuId === folder.id && (
                        <div
                          className="dashboard-folder-item__menu"
                          onMouseLeave={() => setFolderMenuId(null)}
                        >
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setCurrentFolderId(folder.id);
                              setFolderMenuId(null);
                            }}
                          >
                            Abrir
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setRenamingFolderId(folder.id);
                              setRenameFolderName(folder.name);
                              setFolderMenuId(null);
                            }}
                          >
                            Renomear
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openMoveModal("single-folder", folder.id);
                              setFolderMenuId(null);
                            }}
                          >
                            Mover para...
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setShareFolderTarget({
                                id: folder.id,
                                name: folder.name,
                              });
                              setFolderMenuId(null);
                            }}
                          >
                            Compartilhar
                          </button>
                          <button
                            className="danger"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteFolder(folder.id);
                              setFolderMenuId(null);
                            }}
                          >
                            Excluir
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}

              {/* Drawings */}
              {filteredDrawings.length === 0 && filteredFolders.length === 0 ? (
                <div className="dashboard-empty">
                  {search || selectedTags.length > 0
                    ? "Nenhum resultado encontrado com esses filtros"
                    : "Nenhum quadro ainda. Crie seu primeiro!"}
                </div>
              ) : (
                filteredDrawings.map((drawing) => (
                  <DrawingCard
                    key={drawing.id}
                    id={drawing.id}
                    name={drawing.name}
                    thumbnail={drawing.thumbnail}
                    updatedAt={drawing.updated_at}
                    tags={drawing.tags}
                    selectMode={selectMode}
                    selected={isItemSelected("drawing", drawing.id)}
                    onSelect={handleCardSelect}
                    onOpen={onOpenDrawing}
                    onDelete={handleDelete}
                    onDuplicate={handleDuplicate}
                    onRename={handleRename}
                    onManageTags={openTagModal}
                    onMoveTo={(id) => openMoveModal("single-drawing", id)}
                    onShareViewLink={handleShareViewLink}
                    onShareInternal={(id, drawingName) =>
                      setShareInternalTarget({ id, name: drawingName })
                    }
                    onManageAccess={(id, drawingName) =>
                      setAccessTarget({ id, name: drawingName })
                    }
                  />
                ))
              )}
            </div>
          )}

          {!loading &&
            !currentFolderId &&
            !isSearching &&
            !selectMode &&
            (sharedDrawings.length > 0 || sharedFolders.length > 0) && (
              <>
                <div className="dashboard-section-header">
                  <div className="dashboard-section-header__left">
                    <h2 className="dashboard-section-header__title">
                      Compartilhados comigo
                    </h2>
                    <button
                      className="dashboard-section-header__refresh"
                      onClick={refreshShared}
                      disabled={refreshingShared}
                      title="Atualizar compartilhados comigo"
                      aria-label="Atualizar compartilhados comigo"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className={refreshingShared ? "is-spinning" : undefined}
                      >
                        <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
                        <path d="M21 3v5h-5" />
                        <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
                        <path d="M3 21v-5h5" />
                      </svg>
                    </button>
                  </div>
                </div>
                <div className="dashboard-content">
                  {sharedFolders.map((f) => (
                    <div
                      key={f.id}
                      className="dashboard-folder-item dashboard-folder-item--shared"
                      onDoubleClick={() => setBrowsingSharedFolder(f)}
                      title={`Compartilhada por ${
                        f.owner_name || f.owner_email
                      }`}
                    >
                      <div className="dashboard-folder-item__icon">
                        <svg
                          width="32"
                          height="32"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                          stroke="none"
                        >
                          <path d="M2 6a2 2 0 012-2h5l2 2h9a2 2 0 012 2v10a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
                        </svg>
                      </div>
                      <span className="dashboard-folder-item__name">
                        {f.name}
                      </span>
                      <span className="dashboard-folder-item__shared-owner">
                        {f.owner_name || f.owner_email}
                      </span>
                    </div>
                  ))}
                  {sharedDrawings.map((d) => (
                    <SharedDrawingCard
                      key={d.id}
                      drawing={d}
                      onOpen={handleOpenShared}
                      onSaveCopy={handleSaveCopyShared}
                    />
                  ))}
                </div>
              </>
            )}
        </main>
      </div>

      {/* Tag management modal */}
      {tagModalDrawingId && (
        <div className="drawings-overlay" onClick={closeTagModal}>
          <div className="tag-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Gerenciar Tags</h3>
            <p className="tag-modal__subtitle">
              {drawings.find((d) => d.id === tagModalDrawingId)?.name}
            </p>
            <div className="tag-modal__search">
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <circle cx="11" cy="11" r="8" />
                <path d="m21 21-4.35-4.35" />
              </svg>
              <input
                type="text"
                placeholder="Buscar tags..."
                value={tagModalSearch}
                onChange={(e) => setTagModalSearch(e.target.value)}
              />
              {tagModalSearch && (
                <button
                  className="tag-modal__search-clear"
                  onClick={() => setTagModalSearch("")}
                >
                  &times;
                </button>
              )}
            </div>
            <div className="tag-modal__list">
              {tags
                .filter((tag) =>
                  tag.name.toLowerCase().includes(tagModalSearch.toLowerCase()),
                )
                .map((tag) => (
                  <label key={tag.id} className="tag-modal__item">
                    <input
                      type="checkbox"
                      checked={tagModalSelected.includes(tag.id)}
                      onChange={() => toggleTagInModal(tag.id)}
                    />
                    <span
                      className="tag-modal__color"
                      style={{ backgroundColor: tag.color }}
                    />
                    <span>{tag.name}</span>
                  </label>
                ))}
              {tags.length === 0 && (
                <p className="tag-modal__empty">
                  Nenhuma tag criada. Use o botao "+ Tag" na barra de tags para
                  criar.
                </p>
              )}
              {tags.length > 0 &&
                tagModalSearch &&
                tags.filter((t) =>
                  t.name.toLowerCase().includes(tagModalSearch.toLowerCase()),
                ).length === 0 && (
                  <p className="tag-modal__empty">Nenhuma tag encontrada.</p>
                )}
            </div>
            <div className="tag-modal__actions">
              <button className="tag-modal__done" onClick={closeTagModal}>
                Salvar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Move-to modal */}
      {moveModalTarget && (
        <div
          className="drawings-overlay"
          onClick={() => setMoveModalTarget(null)}
        >
          <div className="move-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Mover para...</h3>
            <p className="move-modal__subtitle">
              {moveModalTarget.type === "bulk"
                ? `${selectedItems.length} item${
                    selectedItems.length !== 1 ? "s" : ""
                  } selecionado${selectedItems.length !== 1 ? "s" : ""}`
                : moveModalTarget.type === "single-drawing"
                ? drawings.find((d) => d.id === moveModalTarget.id)?.name
                : folders.find((f) => f.id === moveModalTarget.id)?.name}
            </p>
            <div className="move-modal__tree">
              <button
                className={`move-modal__tree-item ${
                  moveTargetFolderId === null
                    ? "move-modal__tree-item--active"
                    : ""
                }`}
                onClick={() => setMoveTargetFolderId(null)}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                </svg>
                Raiz
              </button>
              {folderTree.map(({ folder, depth }) => {
                const disabled = excludedIds.has(folder.id);
                return (
                  <button
                    key={folder.id}
                    className={`move-modal__tree-item ${
                      moveTargetFolderId === folder.id
                        ? "move-modal__tree-item--active"
                        : ""
                    } ${disabled ? "move-modal__tree-item--disabled" : ""}`}
                    style={{ paddingLeft: `${16 + depth * 20}px` }}
                    onClick={() =>
                      !disabled && setMoveTargetFolderId(folder.id)
                    }
                    disabled={disabled}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="#f0c040"
                      stroke="none"
                    >
                      <path d="M2 6a2 2 0 012-2h5l2 2h9a2 2 0 012 2v10a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
                    </svg>
                    {folder.name}
                  </button>
                );
              })}
            </div>
            <div className="move-modal__actions">
              <button
                className="move-modal__cancel"
                onClick={() => setMoveModalTarget(null)}
              >
                Cancelar
              </button>
              <button
                className="move-modal__confirm"
                onClick={executeMoveToFolder}
              >
                Mover
              </button>
            </div>
          </div>
        </div>
      )}

      {shareLinkCopied && (
        <div className="share-toast">
          Link de visualizacao copiado para a area de transferencia!
        </div>
      )}

      {showProfile && user && (
        <ProfileDialog
          user={user}
          onClose={() => setShowProfile(false)}
          onUserUpdated={(updated) => {
            onUserUpdated?.(updated);
          }}
          onLogout={() => {
            setShowProfile(false);
            handleLogout();
          }}
          onAccountDeleted={() => {
            setShowProfile(false);
            onLogout();
          }}
        />
      )}

      {shareInternalTarget && (
        <ShareInternalDialog
          drawingId={shareInternalTarget.id}
          drawingName={shareInternalTarget.name}
          onClose={() => setShareInternalTarget(null)}
        />
      )}

      {shareFolderTarget && (
        <ShareFolderDialog
          folderId={shareFolderTarget.id}
          folderName={shareFolderTarget.name}
          onClose={() => setShareFolderTarget(null)}
        />
      )}

      {browsingSharedFolder && (
        <SharedFolderBrowser
          root={browsingSharedFolder}
          onOpenDrawing={handleOpenSharedFolderDrawing}
          onSaveCopy={handleSaveCopyShared}
          onClose={() => setBrowsingSharedFolder(null)}
        />
      )}

      {accessTarget && (
        <AccessListDialog
          drawingId={accessTarget.id}
          drawingName={accessTarget.name}
          onClose={() => setAccessTarget(null)}
        />
      )}
    </div>
  );
};
